// frontend/src/routes/RegulatoryWatch.tsx
// Mirrors Renewals.tsx structure: header, filters, grouped list, loading/empty states.
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { useSession } from "../lib/auth";
import { canManageRegulatory } from "../lib/permissions";
import { fetchCandidates, fetchCountries, fetchEntities, fetchRegulatoryLastRun } from "../lib/dataFetchers";
import { relativeTime } from "../lib/relativeTime";
import { groupByDetectedDate } from "../lib/regwatchGrouping";
import { NotExhaustiveBanner } from "../components/regulatory/NotExhaustiveBanner";
import { RegulatoryAlertCard } from "../components/regulatory/RegulatoryAlertCard";
import { SourcesPanel } from "../components/regulatory/SourcesPanel";
import { FilterBar, FilterGroup } from "../components/ui/FilterBar";
import { Select } from "../components/ui/Select";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Button } from "../components/ui/Button";
import type { RuleChangeCandidate, Country } from "../lib/types";

type ReviewState = "active" | "reviewed" | "all";

// How many alert cards to reveal at a time. The feed grows daily and is
// fetched in full (no server pagination), so we cap the rendered DOM and let
// the user reveal more — "Show more" can't desync because it slices the
// already-loaded array.
const REVEAL_STEP = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function confidenceRank(c: string): number {
  return c === "high" ? 3 : c === "med" ? 2 : 1;
}

function sortCandidates(candidates: RuleChangeCandidate[]): RuleChangeCandidate[] {
  return [...candidates].sort((a, b) => {
    const confDiff = confidenceRank(b.confidence) - confidenceRank(a.confidence);
    if (confDiff !== 0) return confDiff;
    return new Date(b.detectedDate).getTime() - new Date(a.detectedDate).getTime();
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RegulatoryWatch(): JSX.Element {
  const { t, i18n } = useTranslation("regulatory");
  const { user } = useSession();
  const canManage = canManageRegulatory(user?.role);

  const [countryFilter, setCountryFilter] = useState<string>("ALL");
  const [reviewState, setReviewState] = useState<ReviewState>("active");

  // Incremental reveal — how many cards to show. Collapses back to the first
  // page whenever the filters change (same deps as the candidates query key).
  const [visibleCount, setVisibleCount] = useState(REVEAL_STEP);
  useEffect(() => setVisibleCount(REVEAL_STEP), [countryFilter, reviewState]);

  // ── Data queries ────────────────────────────────────────────────────────────
  // NOTE: country filter is applied client-side (see `candidates` below). If it
  // were part of the query key, the dropdown's option set (derived from the
  // fetched data) would collapse on selection and unmount itself — the user
  // could then never switch countries or reset to "All" without a reload.
  const { data: allCandidates = [], isLoading, isError } = useQuery({
    queryKey: ["regulatory-candidates", { reviewState }],
    queryFn: () => fetchCandidates({ reviewState }),
    staleTime: 60_000,
  });

  const { data: entitiesResp } = useQuery({
    queryKey: ["entities"],
    queryFn: () => fetchEntities({ limit: 100 }),
    staleTime: 300_000,
  });

  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: () => fetchCountries(),
    staleTime: 300_000,
  });

  const { data: lastRun } = useQuery({
    queryKey: ["regulatory-last-run"],
    queryFn: fetchRegulatoryLastRun,
    staleTime: 60_000,
  });

  // ── Derived data ────────────────────────────────────────────────────────────

  const countryMap = useMemo<Map<string, Country>>(
    () => new Map(countries.map((c) => [c.id, c])),
    [countries],
  );

  // Resolve affectedEntityIds → entity names for the card chips.
  const entityNames = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    for (const e of entitiesResp?.entities ?? []) m[e.id] = e.name;
    return m;
  }, [entitiesResp]);

  // countryFilter dropdown — only countries that are already resolved in countryMap.
  // Derive from the FULL fetched set (allCandidates), NOT the client-filtered
  // `candidates` below — otherwise selecting a country would collapse the option
  // set to length 1 and the `<Select>` would unmount itself.
  const candidateCountryOptions = useMemo<Array<{ iso2: string; label: string }>>(() => {
    const seen = new Set<string>();
    const opts: Array<{ iso2: string; label: string }> = [];
    for (const c of allCandidates) {
      const resolved = countryMap.get(c.countryId);
      if (!resolved) continue;
      if (seen.has(resolved.iso2)) continue;
      seen.add(resolved.iso2);
      opts.push({ iso2: resolved.iso2, label: `${resolved.flag ?? ""} ${resolved.name}` });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [allCandidates, countryMap]);

  // Client-side country filter. Kept out of the query key so the dropdown's
  // options (derived from the full fetched set) stay stable across selections.
  const candidates = useMemo(() => {
    if (countryFilter === "ALL") return allCandidates;
    return allCandidates.filter(
      (c) => countryMap.get(c.countryId)?.iso2 === countryFilter,
    );
  }, [allCandidates, countryFilter, countryMap]);

  // Sort once, then reveal only the first `visibleCount` before grouping, so
  // date groups appear progressively as cards are revealed.
  const sorted = useMemo(() => sortCandidates(candidates), [candidates]);
  const visible = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
  const hasMore = candidates.length > visible.length;

  // Group by detected date (newest day first), preserving sortCandidates order
  // (confidence desc, then detectedDate desc) WITHIN each day bucket.
  const grouped = useMemo(
    () => groupByDetectedDate(visible, i18n.language),
    [visible, i18n.language],
  );

  // ── Derived select options ────────────────────────────────────────────────
  const countrySelectOptions = [
    { value: "ALL", label: t("filter_all") },
    ...candidateCountryOptions.map((o) => ({ value: o.iso2, label: o.label })),
  ];

  const reviewStateOptions: Array<{ value: ReviewState; label: string }> = [
    { value: "active", label: t("review_filter_active") },
    { value: "reviewed", label: t("review_filter_reviewed") },
    { value: "all", label: t("review_filter_all") },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page header */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-[18px]">
        <div>
          <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
            <ShieldAlert className="inline-block w-5 h-5 mr-2 mb-0.5 text-muted" aria-hidden />
            {t("page_title")}
          </h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {t("page_subtitle", { count: candidates.length })}
          </p>
          {/* Last-run status */}
          {lastRun !== undefined && (
            <p className="text-[12px] text-dim mt-1">
              {lastRun.lastCheckedAt === null ? (
                t("last_run_not_yet_run")
              ) : (
                <>
                  {t("last_run_last_checked", { relative: relativeTime(lastRun.lastCheckedAt) })}
                  {lastRun.newToday === 0 && (
                    <> &middot; {t("last_run_no_updates_today")}</>
                  )}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Persistent "not a guarantee" banner — always visible, no dismiss */}
      <div className="mb-4">
        <NotExhaustiveBanner />
      </div>

      {/* Filters */}
      <FilterBar>
        {/* Per-person review filter — Active (default) / Reviewed / All */}
        <FilterGroup label={t("filter_label_review")}>
          <SegmentedControl<ReviewState>
            options={reviewStateOptions}
            value={reviewState}
            onChange={setReviewState}
            label={t("filter_label_review")}
          />
        </FilterGroup>

        {/* Country filter — only countries already resolved in countryMap */}
        {candidateCountryOptions.length > 1 && (
          <FilterGroup label={t("filter_label_country")}>
            <Select
              value={countryFilter}
              onValueChange={setCountryFilter}
              options={countrySelectOptions}
              placeholder={t("filter_all")}
              minWidth="min-w-[180px]"
            />
          </FilterGroup>
        )}
      </FilterBar>

      {/* Sources panel — manager-only */}
      {canManage && (
        <SourcesPanel
          countryFilterIso2={countryFilter === "ALL" ? undefined : countryFilter}
          countries={countries}
          countryMap={countryMap}
        />
      )}

      {/* Main content */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-40" />
          ))}
        </div>
      ) : isError ? (
        <div
          className="text-dim text-[13px] p-[18px] text-center border border-dashed border-border rounded-[9px] bg-surface"
        >
          {t("generic_error")}
        </div>
      ) : grouped.length === 0 ? (
        <div
          className="text-dim text-[13px] p-[18px] text-center border border-dashed border-border rounded-[9px] bg-surface space-y-2"
        >
          {reviewState === "reviewed" ? (
            <p>{t("empty_state_reviewed")}</p>
          ) : (
            <>
              <p>{reviewState === "active" ? t("empty_state_active") : t("empty_state")}</p>
              {countryFilter === "ALL" && reviewState !== "active" && (
                <p className="text-[12px]">{t("empty_state_all")}</p>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <section className="space-y-6">
            {grouped.map(({ label, dateKey, items }) => (
              <section key={dateKey} aria-labelledby={`day-${dateKey}`}>
                {/* Day group header — proto .month-h style */}
                <div className="flex items-center gap-[9px] mb-2">
                  <h2
                    id={`day-${dateKey}`}
                    className="text-[12.5px] font-bold text-muted"
                  >
                    {label}
                  </h2>
                  <span className="ml-auto text-[11px] text-dim bg-surface-2 rounded-full px-[9px] py-[2px]">
                    {items.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {items.map((candidate) => (
                    <RegulatoryAlertCard
                      key={candidate.id}
                      candidate={candidate}
                      country={countryMap.get(candidate.countryId) ?? null}
                      entityNames={entityNames}
                    />
                  ))}
                </div>
              </section>
            ))}
          </section>

          {hasMore && (
            <div className="mt-5 flex flex-col items-center gap-2">
              <p className="text-[12px] text-dim tabular-nums">
                {t("reveal_showing", { shown: visible.length, total: candidates.length })}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setVisibleCount((c) => c + REVEAL_STEP)}
              >
                {t("reveal_show_more")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
